// All engine times are calendar days, counted from 1970-01-01, from `dayIn`.
export const MS_PER_DAY = 24 * 60 * 60 * 1000;
// The UTC instant a day starts at: the engine's own instants, such as
// interest history rows.
export const startOf = (day) => day * MS_PER_DAY;
export const isoDate = (day) => new Date(startOf(day)).toISOString();
// No end: a day no time reaches.
export const NEVER = Number.POSITIVE_INFINITY;
// Memo caches are cleared when they grow past this, as days recur but the
// process is long-lived.
const MEMO_LIMIT = 100_000;
const dayFunctions = new Map();
// The tenant's calendar day of a date: stored dates are instants (Danish
// midnight is 22:00 or 23:00 UTC the day before), so their UTC date is wrong.
// Strings without a time are calendar days already. Memoised: payment plans
// replay the same vouchers many times.
export const dayIn = (timezone) => {
    let dayOf = dayFunctions.get(timezone);
    if (dayOf === undefined) {
        // en-CA formats as YYYY-MM-DD.
        const formatter = new Intl.DateTimeFormat('en-CA', {
            timeZone: timezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
        const days = new Map();
        dayOf = (date) => {
            const key = typeof date === 'string' ? date : date.toISOString();
            let day = days.get(key);
            if (day === undefined) {
                day =
                    Date.parse(key.length === 10 ? key : formatter.format(new Date(key))) / MS_PER_DAY;
                if (days.size > MEMO_LIMIT) {
                    days.clear();
                }
                days.set(key, day);
            }
            return day;
        };
        dayFunctions.set(timezone, dayOf);
    }
    return dayOf;
};
const MS_PER_HOUR = 60 * 60 * 1000;
const offsetFormatters = new Map();
// How far the wall clock in `timezone` is ahead of UTC at `instant`.
const offsetAt = (timezone, instant) => {
    let formatter = offsetFormatters.get(timezone);
    if (formatter === undefined) {
        formatter = new Intl.DateTimeFormat('en-CA', {
            timeZone: timezone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        offsetFormatters.set(timezone, formatter);
    }
    const parts = Object.fromEntries(formatter
        .formatToParts(instant)
        .map((part) => [part.type, Number(part.value)]));
    const wallClock = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    return wallClock - Math.floor(instant / 1000) * 1000;
};
// The instant a day starts at in the tenant's timezone, as stored dates are
// written outside the engine, so `dayIn` reads it back as the same day.
export const isoDateIn = (timezone) => {
    const dayOf = dayIn(timezone);
    return (day) => {
        const midnight = startOf(day);
        let instant = midnight - offsetAt(timezone, midnight);
        instant = midnight - offsetAt(timezone, instant);
        // Where a clock change skips midnight, the day starts after it.
        while (dayOf(new Date(instant)) < day) {
            instant += MS_PER_HOUR;
        }
        return new Date(instant).toISOString();
    };
};
// The instant a day picked in the browser starts in the tenant's timezone.
// Pickers give the browser's midnight, which west of the browser (a Danish
// creditor on a Greenlandic tenant) is the day before.
export const startOfPickedDayIn = (timezone) => {
    const tenantMidnight = isoDateIn(timezone);
    return (picked) => tenantMidnight(Date.UTC(picked.getFullYear(), picked.getMonth(), picked.getDate()) /
        MS_PER_DAY);
};
// The browser date showing the tenant's calendar day of a stored date, so a
// picker shows it, and `startOfPickedDayIn` stores it, as that day.
export const pickedDayIn = (timezone) => {
    const dayOf = dayIn(timezone);
    return (date) => {
        const tenantDay = new Date(startOf(dayOf(date)));
        return new Date(tenantDay.getUTCFullYear(), tenantDay.getUTCMonth(), tenantDay.getUTCDate());
    };
};
const daysInMonth = (year, month) => new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
const addYearsCache = new Map();
// Calendar years, clamping Feb 29 to Feb 28 like moment. Memoised: the same
// few days and periods recur throughout a replay.
export const addYears = (day, years) => {
    let byDay = addYearsCache.get(years);
    if (byDay === undefined) {
        byDay = new Map();
        addYearsCache.set(years, byDay);
    }
    let result = byDay.get(day);
    if (result === undefined) {
        const date = new Date(startOf(day));
        const year = date.getUTCFullYear() + years;
        const month = date.getUTCMonth();
        result =
            Date.UTC(year, month, Math.min(date.getUTCDate(), daysInMonth(year, month))) / MS_PER_DAY;
        if (byDay.size > MEMO_LIMIT) {
            byDay.clear();
        }
        byDay.set(day, result);
    }
    return result;
};
// First day d whose limitation `addYears(d, years)` has not passed at `at`;
// every day before it has expired.
export const expiryCut = (at, years) => {
    let day = addYears(at, -years);
    while (addYears(day, years) < at) {
        day++;
    }
    while (addYears(day - 1, years) >= at) {
        day--;
    }
    return day;
};
export const monthStart = (day) => {
    const date = new Date(startOf(day));
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1) / MS_PER_DAY;
};
export const quarterStart = (day) => {
    const date = new Date(startOf(day));
    return (Date.UTC(date.getUTCFullYear(), Math.floor(date.getUTCMonth() / 3) * 3, 1) /
        MS_PER_DAY);
};
const nextMonthStarts = new Map();
export const nextMonthStart = (day) => {
    let result = nextMonthStarts.get(day);
    if (result === undefined) {
        const date = new Date(startOf(day));
        result =
            Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1) / MS_PER_DAY;
        if (nextMonthStarts.size > MEMO_LIMIT) {
            nextMonthStarts.clear();
        }
        nextMonthStarts.set(day, result);
    }
    return result;
};
//# sourceMappingURL=dates.js.map